import get from 'lodash/get';
import isObject from 'lodash/isObject';
import upperFirst from 'lodash/upperFirst';
import hasProp from './hasProp';
import { locationDisplayName } from './locations';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

export const defaultData = {
	rules: [],
	operators: [],
	actions: [],
	shopifyRules: [],
	wooCommerceRules: [],
	bigCommerceRules: [],
	eddRules: [],
};

export const setup = (rulesetData = {}) => {
	rulesetData = {
		...defaultData,
		...rulesetData,
	};

	const getItemObject = (item, itemType = 'rule') => {
		const data =
			'rule' === itemType
				? [
						...rulesetData.rules,
						...rulesetData.shopifyRules,
						...rulesetData.wooCommerceRules,
						...rulesetData.bigCommerceRules,
						...rulesetData.eddRules,
				  ]
				: rulesetData[`${itemType}s`];

		return data.find((i) => i.id === item.type);
	};

	const getObjDisplayText = (obj) => {
		return get(obj, 'displayText', '');
	};

	const getItemOperator = (item) => {
		if (item && item.operator && '0' !== item.operator) {
			return rulesetData.operators.find((o) => o.id === item.operator) || {};
		}

		return {};
	};

	const getItemOperatorText = (item) => {
		return getObjDisplayText(getItemOperator(item));
	};

	const isCampaignRule = (item) => {
		if (item && item.type) {
			switch (item.type) {
				case 'campaign-optin':
				case 'campaign-not-optin':
				case 'campaign-closed':
				case 'campaign-not-closed':
				case 'campaign-not-seen':
				case 'campaign-seen': {
					return true;
				}
				default:
					return false;
			}
		}

		return false;
	};

	const isVisitedRule = (i) => ['has-visited', 'has-not-visited'].includes(get(i, 'type', ''));

	/**
	 * Determines if an item has hinted that it doesn't have a value.
	 *
	 * This is not an explicit determination whether or not the item _will_ have
	 * a value. It's only a quick check to see if it _could_ have a value.
	 *
	 * NOTE: This function is highly targeted for use in `getItemValue`, and may
	 *       not be usable outside of this context.
	 *
	 * @param {Object} item     The item object.
	 * @param {Object} itemInfo The item info object.
	 *
	 * @returns {bool} False if the item has hinted it doesn't have a value.
	 */
	const itemCanHaveValue = (item, itemInfo) => {
		return (
			// If the item is a key/value rule, the key or value may have a value.
			(isObject(item.value) && 'KeyValue' === get(itemInfo, 'valueType')) ||
			// If an item is an action, treat it as if it has a value.
			['show-campaign', 'display-effect', 'sound-effect'].includes(item.type) ||
			// If the item is any ecommerce product rule
			isEcommerceEntityRule(item) ||
			// The item has hinted that it has a value.
			getItemOperator(item).hasValue
		);
	};

	/**
	 * Determines if an item has hinted that it shouldn't show unit.
	 *
	 * @param {Array}  units The units array.
	 * @param {Object} item  The item object.
	 *
	 * @returns {bool} False if item has hinted it shouldn't show unit.
	 */
	const itemCanShowUnit = (units, item) => {
		return (
			units.length &&
			false !== getItemOperator(item).canShowUnit &&
			false !== getItemOperator(get(item, 'value.value')).canShowUnit
		);
	};

	const getKeyValueSummary = (item, itemInfo) => {
		return [
			getItemOperatorText(item.value.key),
			getItemValue(item.value.key, itemInfo),
			getItemOperatorText(item.value.value),
			getItemValue(item.value.value, itemInfo),
		];
	};

	const getItemValue = (item, itemInfo = {}, campaignList = [], loading = false) => {
		if (!item) {
			return 'Unknown';
		}

		// Return early if the item has hinted that it doesn't have a value.
		if (!itemCanHaveValue(item, itemInfo)) {
			return '';
		}

		if (isCampaignRule(item)) {
			if (loading) {
				return '...loading';
			}

			return getCampaignFriendlyText(item.value, campaignList);
		}

		if (isEcommerceEntityRule(item)) {
			return getEcommerceEntityRuleFriendlyText(item);
		}

		switch (item.type) {
			case 'query-arg':
			case 'cookie':
			case 'local-storage':
			case 'session-storage':
			case 'js-variable': {
				const [keyOperator, keyValue, valueOperator, valueValue] = getKeyValueSummary(item, itemInfo);

				// format return string appropriately
				return `key ${keyOperator} ${keyValue} and value ${valueOperator} ${valueValue}`;
			}
			case 'has-visited':
			case 'has-not-visited': {
				// Check for legacy has visited rules, update to key value pair
				if ('has-visited' === item.type && 'string' === typeof item.value) {
					item.value = {
						key: {
							operator: item.operator,
							value: item.value,
						},
						value: {
							operator: itemInfo.valueOperatorDefault,
							value: 30,
						},
					};
				}

				return getKeyValueSummary(item, itemInfo).join(' ');
			}
			case 'exit-intent':
				return '';
			case 'geolocation':
				return locationDisplayName(item.value) || item.value;
			case 'display-effect':
			case 'sound-effect':
			case 'show-campaign': {
				if ('yesno' === item.value) {
					return 'Yes/No';
				}
				return item.value ? upperFirst(item.value) : item.value;
			}
			case 'day': {
				if (isNaN(item.value)) {
					if ('string' === typeof item.value) {
						item.value = [item.value];
					}
					const days = item.value.map((d) => {
						return d ? upperFirst(d) : '';
					});

					return `${days.join(', ')}`;
				} else {
					const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
					return days[item.value] || '';
				}
			}
			case 'month': {
				if ('string' === typeof item.value) {
					item.value = [item.value];
				}
				const months = item.value.map((d) => {
					return d ? upperFirst(d) : '';
				});

				return `in ${months.join(', ')}`;
			}
			case 'page-time':
			case 'site-time': {
				const isImmediate = 'as-soon-as-possible' === item.operator;
				const pageSiteTimeDefault = isImmediate ? '' : '0';
				return item.value && !isImmediate ? item.value : pageSiteTimeDefault;
			}
			case 'date': {
				return format(parseISO(item.value), 'MMMM do, yyyy');
			}
			case 'date-time': {
				return format(parseISO(item.value), 'MMMM do, yyyy, h:mmaaa');
			}
			case 'device': {
				if ('string' === typeof item.value) {
					item.value = [item.value];
				}
				const result = item.value
					? get(itemInfo, 'valueSource', []).filter((v) => item.value.includes(v.value))
					: false;

				const devices =
					result && result.length
						? result.map((d) => {
								return d.label ? upperFirst(d.label) : '';
						  })
						: ['(no value provided)'];

				return `${devices.join(' or ')}`;
			}
			default:
				// If the item's operator specifies that it does not have a value,
				// do not show one, even if it exists (like if they changed the operator)
				if (itemInfo && hasProp(itemInfo, 'hasValue') && !itemInfo.hasValue) {
					return '';
				}

				// @todo Figure out how to make boolean values actually save in the DB. This will break things.
				return itemCanHaveValue(item, itemInfo) ? item.value : '';
		}
	};

	const getItemUnit = (item, itemObject) => {
		let units = get(itemObject, 'valueUnits', []);

		// Check for key value pair value operator level units.
		if (get(itemObject, 'valueValueUnits', []).length) {
			units = get(itemObject, 'valueValueUnits', []);
		}

		// If there are no units, and the operator has hinted that it shouldn't
		// show a unit, return early.
		if (!itemCanShowUnit(units, item)) {
			return '';
		}

		// Set the default label.
		let label = '';

		if ((item.unit && '0' !== item.unit) || ['date-time', 'time'].includes(item.type)) {
			// If the rule is a time or date time, and unit value is '0', set it
			// to an empty string, for "Visitor's Local Time".
			if (['date-time', 'time'].includes(item.type) && '0' === item.unit) {
				item.unit = '';
			}

			// Get the unit object, and, if present, the label.
			const unit = units.find((u) => u.value === item.unit);
			if (unit && unit.label) {
				label = unit.label;
			}
		} else if (isVisitedRule(item)) {
			label = units[0].label;
		} else {
			label = units[0].shortUnit;
		}

		return label;
	};

	const getRuleFriendlyText = ({ data, itemType = 'rule', folders = [], campaigns = [], loading = false }) => {
		const itemOperator = getItemOperator(data);
		const itemObject = getItemObject(data, itemType);
		const outputItems = {
			output: getObjDisplayText(itemObject),
			opText: getObjDisplayText(itemOperator),
			valText: getItemValue(
				data,
				{ ...itemOperator, ...itemObject },
				'opted-folder' === itemOperator.id ? folders : campaigns,
				loading
			),
			unitText: getItemUnit(data, itemObject),
		};

		return Object.values(filterFriendlyText(outputItems, data, itemType))
			.filter((s) => '' !== s)
			.join(' ')
			.trim();
	};

	/**
	 * Allow items used for friendly text to be filtered, before being merged
	 * into one output string.
	 *
	 * @param {Object} outputItems An object of the items for output.
	 * @param {Object} data        The rule data object.
	 *
	 * @returns {Object} The filtered `outputItems` object.
	 */
	const filterFriendlyText = (outputItems, data) => {
		// Prevent double operator output for legacy `has-visited` rules.
		if (isVisitedRule(data)) {
			// has visited used to not be key value pair, this prevents legacy rules from showing an operator by mistake
			outputItems.opText = '';
		}

		return outputItems;
	};

	/**
	 * For campaign interaction rules, replaces the ID value with the friendlier
	 * easy-to-read version from the campaign list if available. Otherwise,
	 * returns a loading string.
	 *
	 * @param {Array|string} value        The original value that we want to replace.
	 * @param {Array}        campaignList A list of campaigns that we can search through for the value.
	 *
	 * @returns {string} The string to show on the summary page for the value
	 */
	const getCampaignFriendlyText = (value, campaignList) => {
		const campaignObjectList = campaignList.filter((element) => {
			if (!element.label) {
				element.label = element.name;
			}

			if (Array.isArray(value)) {
				return value.includes(element.id) || value.includes(element.value);
			}

			return element.value === value || element.id === value;
		});
		if (0 < campaignObjectList.length) {
			return ': ' + campaignObjectList.map((elem) => elem.label.replace(' - ' + elem.value, '')).join(', ');
		}

		return '(no campaign selected)';
	};

	/**
	 * Search through list of items for search term.
	 *
	 * Uses item displayText, friendlyText, helpText, id, and keywords
	 * as search source.
	 *
	 * @since 0.1.25
	 *
	 * @param {string} searchTerm The term to match rules against.
	 * @param {Array}  items      Items to loop through and search.
	 *
	 * @returns {Array} Array of items which match the search term.
	 */
	const searchItems = (searchTerm, items) => {
		if (!searchTerm) {
			return items;
		}

		searchTerm = searchTerm.toLowerCase();
		return items.filter((item) => {
			const toSearch = [
				getObjDisplayText(item).toLowerCase(),
				get(item, 'friendlyText', '').toLowerCase(),
				get(item, 'helpText', '').toLowerCase(),
				get(item, 'id', '').toLowerCase(),
				get(item, 'keywords', []).join('|'),
			].join('|');

			return toSearch.includes(searchTerm);
		});
	};

	/**
	 * Parses the ecommerce entity rule and returns the rule friendly text.
	 *
	 * @since 0.1.42
	 *
	 * @param {Object} item The item to check.
	 *
	 * @returns {string} The friendly text for the item.
	 */
	const getEcommerceEntityRuleFriendlyText = (item) => {
		return get(item.value, 'label', item.value);
	};

	/**
	 * Checks if a rule is a provider entity rule
	 *
	 * @since 0.1.42
	 *
	 * @param {Object} item     The item to check.
	 * @param {string} provider The provider to check.
	 *
	 * @returns {boolean} Whether rule is a provider entity rule or not.
	 */
	const baseIsEntityRule = (item, provider) => {
		const rule = getItemObject(item);
		const platform = get(rule, 'platform', '').toLowerCase();
		const entity = get(rule, 'valueSource.entity');
		const ruleType = get(rule, 'ruleType', []);

		return !!(rule && platform === provider && ruleType.includes('ecommerce') && entity);
	};

	/**
	 * Checks if a rule is a Woocommerce entity rule
	 *
	 * @since 0.1.42
	 *
	 * @param {Object} item The item to check.
	 *
	 * @returns {boolean} Whether rule is Woocommerce entity or not.
	 */
	const isWoocommerceEntityRule = (item) => {
		return baseIsEntityRule(item, 'woocommerce');
	};

	/**
	 * Checks if a rule is a Shopify entity rule
	 *
	 * @since 0.1.42
	 *
	 * @param {Object} item The item to check.
	 *
	 * @returns {boolean} Whether rule is Shopify entity or not.
	 */
	const isShopifyEntityRule = (item) => {
		return baseIsEntityRule(item, 'shopify');
	};

	/**
	 * Checks if a rule is a BigCommerce entity rule
	 *
	 * @since 0.1.42
	 *
	 * @param {Object} item The item to check.
	 *
	 * @returns {boolean} Whether item is BigCommerce entity or not.
	 */
	const isBigcommerceEntityRule = (item) => {
		return baseIsEntityRule(item, 'bigcommerce');
	};

	/**
	 * Checks if a rule is a Edd entity rule
	 *
	 * @since 0.1.43
	 *
	 * @param {Object} item The item to check.
	 *
	 * @returns {boolean} Whether rule is EDD entity or not.
	 */
	const isEddEntityRule = (item) => {
		return baseIsEntityRule(item, 'edd');
	};

	/**
	 * Checks if an item is an Ecommerce entity rule
	 *
	 * @since 0.1.42
	 *
	 * @param {Object} item The item to check.
	 *
	 * @returns {boolean} Whether rule is ecommerce entity or not.
	 */
	const isEcommerceEntityRule = (item) => {
		return (
			isWoocommerceEntityRule(item) ||
			isShopifyEntityRule(item) ||
			isBigcommerceEntityRule(item) ||
			isEddEntityRule(item)
		);
	};

	return {
		...rulesetData,
		getItemObject,
		getObjDisplayText,
		getItemOperator,
		getItemOperatorText,
		isCampaignRule,
		getItemValue,
		getItemUnit,
		getRuleFriendlyText,
		searchItems,
		isWoocommerceEntityRule,
		isShopifyEntityRule,
		isBigcommerceEntityRule,
		isEddEntityRule,
		isEcommerceEntityRule,
		getEcommerceEntityRuleFriendlyText,
	};
};
